import React from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, UserCheck } from "lucide-react";

const FIELD_LABELS = {
  phone: "Phone number",
  emergency_contact_name: "Emergency contact name",
  emergency_contact_phone: "Emergency contact phone",
  address_street: "Street address",
  address_city: "City",
  address_postal: "Postcode",
  address_country: "Country",
  airport_preference: "Airport preference",
  passport_number: "Passport number",
  passport_expiry: "Passport expiry",
  passport_upload: "Passport document upload",
};

export default function ProfileSetupPrompt({ user }) {
  const navigate = useNavigate();

  const handleCompleteProfile = () => {
    navigate("/profile");
  };

  if (!user || user.profile_complete) {
    return null;
  }

  let missingFields = user.profile_missing_fields || [];

  if (missingFields.length === 0 && !user.profile_complete) {
    const checks = {
      phone: user.phone,
      emergency_contact_name: user.emergency_contact_name,
      emergency_contact_phone: user.emergency_contact_phone,
      address_street: user.address_street,
      address_city: user.address_city,
      address_postal: user.address_postal,
      address_country: user.address_country,
      airport_preference: user.airport_preference,
      passport_number: user.passport_number,
      passport_expiry: user.passport_expiry,
    };
    missingFields = Object.entries(checks)
      .filter(([, v]) => !v || (typeof v === 'string' && v.trim() === ''))
      .map(([k]) => k);
  }

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md border-2 border-[rgb(var(--brand-yellow))] bg-white dark:bg-gray-950 [&>button.absolute]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[rgb(var(--brand-yellow))] to-[rgb(var(--brand-yellow-hover))] rounded-full flex items-center justify-center shadow-lg">
              <UserCheck className="w-8 h-8 text-black" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl dark:text-white">
            Welcome to Seiko Sports Technologies!
          </DialogTitle>
          <DialogDescription className="text-center text-base dark:text-gray-300">
            Before you can access events, please complete your profile setup
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-amber-50 dark:bg-amber-950 p-4 rounded-xl border-2 border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-semibold mb-2">Missing Information:</p>
                <ul className="space-y-1 list-disc list-inside">
                  {missingFields.map((field) => (
                    <li key={field}>{FIELD_LABELS[field] || field}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <Button
          onClick={handleCompleteProfile}
          className="w-full bg-[rgb(var(--brand-yellow))] hover:bg-[rgb(var(--brand-yellow-hover))] text-black font-semibold rounded-xl h-12 text-lg"
        >
          Complete My Profile
        </Button>

        <p className="text-xs text-center text-gray-500 dark:text-gray-400">
          This information helps event managers coordinate logistics and ensures your safety during events
        </p>
      </DialogContent>
    </Dialog>
  );
}